import type { UTCTimestamp, LineData, HistogramData } from 'lightweight-charts';
import type { Candle } from '../types/market';

export function smaSeries(candles: Candle[], period: number): LineData<UTCTimestamp>[] {
  if (candles.length < period) return [];
  const values: LineData<UTCTimestamp>[] = [];
  for (let i = period - 1; i < candles.length; i++) {
    const sum = candles.slice(i - period + 1, i + 1).reduce((acc, c) => acc + c.close, 0);
    values.push({ time: candles[i].time as UTCTimestamp, value: sum / period });
  }
  return values;
}

export function emaSeries(candles: Candle[], period: number): LineData<UTCTimestamp>[] {
  if (candles.length < period) return [];
  const multiplier = 2 / (period + 1);
  let ema = candles.slice(0, period).reduce((sum, candle) => sum + candle.close, 0) / period;
  const values: LineData<UTCTimestamp>[] = [{ time: candles[period - 1].time as UTCTimestamp, value: ema }];
  for (let i = period; i < candles.length; i++) {
    ema = (candles[i].close - ema) * multiplier + ema;
    values.push({ time: candles[i].time as UTCTimestamp, value: ema });
  }
  return values;
}

export function emaRaw(data: number[], period: number): number[] {
  if (data.length < period) return [];
  const multiplier = 2 / (period + 1);
  let ema = data.slice(0, period).reduce((sum, val) => sum + val, 0) / period;
  const res = new Array(period - 1).fill(NaN);
  res.push(ema);
  for (let i = period; i < data.length; i++) {
    ema = (data[i] - ema) * multiplier + ema;
    res.push(ema);
  }
  return res;
}

export function vwapSeriesData(candles: Candle[]): LineData<UTCTimestamp>[] {
  let cumulativePv = 0;
  let cumulativeVolume = 0;
  return candles.map((candle) => {
    const typical = (candle.high + candle.low + candle.close) / 3;
    cumulativePv += typical * candle.volume;
    cumulativeVolume += candle.volume;
    return { time: candle.time as UTCTimestamp, value: cumulativeVolume > 0 ? cumulativePv / cumulativeVolume : candle.close };
  });
}

export function bollingerBandsData(candles: Candle[], period: number, stdDev: number) {
  if (candles.length < period) return { upper: [], lower: [], middle: [] };
  const upper: LineData<UTCTimestamp>[] = [];
  const lower: LineData<UTCTimestamp>[] = [];
  const middle: LineData<UTCTimestamp>[] = [];
  
  for (let i = period - 1; i < candles.length; i++) {
    const slice = candles.slice(i - period + 1, i + 1);
    const sum = slice.reduce((acc, c) => acc + c.close, 0);
    const mean = sum / period;
    const variance = slice.reduce((acc, c) => acc + Math.pow(c.close - mean, 2), 0) / period;
    const std = Math.sqrt(variance);
    
    const time = candles[i].time as UTCTimestamp;
    middle.push({ time, value: mean });
    upper.push({ time, value: mean + stdDev * std });
    lower.push({ time, value: mean - stdDev * std });
  }
  return { upper, lower, middle };
}

export function rsiData(candles: Candle[], period: number): LineData<UTCTimestamp>[] {
  if (candles.length <= period) return [];
  let avgGain = 0, avgLoss = 0;
  for (let i = 1; i <= period; i++) {
    const change = candles[i].close - candles[i - 1].close;
    if (change > 0) avgGain += change;
    else avgLoss -= change;
  }
  avgGain /= period;
  avgLoss /= period;
  
  const res: LineData<UTCTimestamp>[] = [];
  let rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
  res.push({ time: candles[period].time as UTCTimestamp, value: 100 - (100 / (1 + rs)) });

  for (let i = period + 1; i < candles.length; i++) {
    const change = candles[i].close - candles[i - 1].close;
    const gain = change > 0 ? change : 0;
    const loss = change < 0 ? -change : 0;
    avgGain = (avgGain * (period - 1) + gain) / period; // Wilder's smoothing
    avgLoss = (avgLoss * (period - 1) + loss) / period;
    rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
    res.push({ time: candles[i].time as UTCTimestamp, value: 100 - (100 / (1 + rs)) });
  }
  return res;
}

export function rsiRaw(candles: Candle[], period: number): number[] {
  if (candles.length <= period) return [];
  let avgGain = 0, avgLoss = 0;
  for (let i = 1; i <= period; i++) {
    const change = candles[i].close - candles[i - 1].close;
    if (change > 0) avgGain += change;
    else avgLoss -= change;
  }
  avgGain /= period;
  avgLoss /= period;
  
  const res = new Array(period).fill(NaN);
  let rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
  res.push(100 - (100 / (1 + rs)));

  for (let i = period + 1; i < candles.length; i++) {
    const change = candles[i].close - candles[i - 1].close;
    const gain = change > 0 ? change : 0;
    const loss = change < 0 ? -change : 0;
    avgGain = (avgGain * (period - 1) + gain) / period;
    avgLoss = (avgLoss * (period - 1) + loss) / period;
    rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
    res.push(100 - (100 / (1 + rs)));
  }
  return res;
}

export function stochasticRsiData(candles: Candle[], rsiPeriod: number, stochPeriod: number, kPeriod: number, dPeriod: number) {
  const rsi = rsiRaw(candles, rsiPeriod);
  const stochRsiRaw: number[] = new Array(candles.length).fill(NaN);
  
  for (let i = rsiPeriod + stochPeriod; i < candles.length; i++) {
    const slice = rsi.slice(i - stochPeriod + 1, i + 1);
    const min = Math.min(...slice);
    const max = Math.max(...slice);
    stochRsiRaw[i] = max === min ? 0 : (rsi[i] - min) / (max - min) * 100;
  }

  // Calculate K (SMA of StochRSI)
  const kLine: LineData<UTCTimestamp>[] = [];
  const kValues = new Array(candles.length).fill(NaN);
  for (let i = rsiPeriod + stochPeriod + kPeriod - 1; i < candles.length; i++) {
    const sum = stochRsiRaw.slice(i - kPeriod + 1, i + 1).reduce((a, b) => a + b, 0);
    const k = sum / kPeriod;
    kValues[i] = k;
    kLine.push({ time: candles[i].time as UTCTimestamp, value: k });
  }

  // Calculate D (SMA of K)
  const dLine: LineData<UTCTimestamp>[] = [];
  for (let i = rsiPeriod + stochPeriod + kPeriod + dPeriod - 2; i < candles.length; i++) {
    const sum = kValues.slice(i - dPeriod + 1, i + 1).reduce((a, b) => a + b, 0);
    dLine.push({ time: candles[i].time as UTCTimestamp, value: sum / dPeriod });
  }
  
  return { k: kLine, d: dLine };
}

export function macdData(candles: Candle[], fast: number, slow: number, signal: number) {
  const closes = candles.map(c => c.close);
  const fastEma = emaRaw(closes, fast);
  const slowEma = emaRaw(closes, slow);
  
  const macdRaw = new Array(candles.length).fill(NaN);
  const macdLine: LineData<UTCTimestamp>[] = [];
  for (let i = slow - 1; i < candles.length; i++) {
    const val = fastEma[i] - slowEma[i];
    macdRaw[i] = val;
    macdLine.push({ time: candles[i].time as UTCTimestamp, value: val });
  }

  // Calculate signal
  const validMacd = macdRaw.filter(v => !isNaN(v));
  const signalEma = emaRaw(validMacd, signal);
  
  const signalLine: LineData<UTCTimestamp>[] = [];
  const histogram: HistogramData<UTCTimestamp>[] = [];
  
  const offset = candles.length - validMacd.length;
  for (let i = signal - 1; i < validMacd.length; i++) {
    const time = candles[i + offset].time as UTCTimestamp;
    const macdVal = validMacd[i];
    const sigVal = signalEma[i];
    signalLine.push({ time, value: sigVal });
    
    const histVal = macdVal - sigVal;
    histogram.push({ 
      time, 
      value: histVal, 
      color: histVal >= 0 ? '#26a69a' : '#ef5350' 
    });
  }
  
  return { macd: macdLine, signal: signalLine, histogram };
}

export function awesomeOscillatorData(candles: Candle[], fast: number = 5, slow: number = 34): HistogramData<UTCTimestamp>[] {
  const medians = candles.map(c => (c.high + c.low) / 2);
  const res: HistogramData<UTCTimestamp>[] = [];
  
  for (let i = slow - 1; i < candles.length; i++) {
    const fastSum = medians.slice(i - fast + 1, i + 1).reduce((a, b) => a + b, 0);
    const slowSum = medians.slice(i - slow + 1, i + 1).reduce((a, b) => a + b, 0);
    const val = (fastSum / fast) - (slowSum / slow);
    
    const prevVal = res.length > 0 ? res[res.length - 1].value : 0;
    const color = val >= prevVal ? '#26a69a' : '#ef5350';
    
    res.push({ time: candles[i].time as UTCTimestamp, value: val, color });
  }
  return res;
}

export function mfiData(candles: Candle[], period: number = 14): LineData<UTCTimestamp>[] {
  if (candles.length <= period) return [];
  const typicalPrice = candles.map(c => (c.high + c.low + c.close) / 3);
  const rawMoneyFlow = typicalPrice.map((tp, i) => tp * candles[i].volume);
  
  const res: LineData<UTCTimestamp>[] = [];
  
  for (let i = period; i < candles.length; i++) {
    let posFlow = 0, negFlow = 0;
    for (let j = i - period + 1; j <= i; j++) {
      if (typicalPrice[j] > typicalPrice[j - 1]) posFlow += rawMoneyFlow[j];
      else if (typicalPrice[j] < typicalPrice[j - 1]) negFlow += rawMoneyFlow[j];
    }
    const mfi = negFlow === 0 ? 100 : 100 - (100 / (1 + (posFlow / negFlow)));
    res.push({ time: candles[i].time as UTCTimestamp, value: mfi });
  }
  return res;
}

export function dmiData(candles: Candle[], period: number = 14) {
  if (candles.length <= period) return { plusDI: [], minusDI: [], adx: [] };
  
  const tr = new Array(candles.length).fill(0);
  const plusDM = new Array(candles.length).fill(0);
  const minusDM = new Array(candles.length).fill(0);

  for (let i = 1; i < candles.length; i++) {
    const highDiff = candles[i].high - candles[i - 1].high;
    const lowDiff = candles[i - 1].low - candles[i].low;
    
    plusDM[i] = highDiff > lowDiff && highDiff > 0 ? highDiff : 0;
    minusDM[i] = lowDiff > highDiff && lowDiff > 0 ? lowDiff : 0;
    
    tr[i] = Math.max(
      candles[i].high - candles[i].low,
      Math.abs(candles[i].high - candles[i - 1].close),
      Math.abs(candles[i].low - candles[i - 1].close)
    );
  }

  let smoothedTR = tr.slice(1, period + 1).reduce((a, b) => a + b, 0);
  let smoothedPlusDM = plusDM.slice(1, period + 1).reduce((a, b) => a + b, 0);
  let smoothedMinusDM = minusDM.slice(1, period + 1).reduce((a, b) => a + b, 0);

  const plusDILine: LineData<UTCTimestamp>[] = [];
  const minusDILine: LineData<UTCTimestamp>[] = [];
  const dx = new Array(candles.length).fill(NaN);

  for (let i = period; i < candles.length; i++) {
    if (i > period) {
      smoothedTR = smoothedTR - (smoothedTR / period) + tr[i];
      smoothedPlusDM = smoothedPlusDM - (smoothedPlusDM / period) + plusDM[i];
      smoothedMinusDM = smoothedMinusDM - (smoothedMinusDM / period) + minusDM[i];
    }
    const plusDI = smoothedTR === 0 ? 0 : 100 * (smoothedPlusDM / smoothedTR);
    const minusDI = smoothedTR === 0 ? 0 : 100 * (smoothedMinusDM / smoothedTR);
    plusDILine.push({ time: candles[i].time as UTCTimestamp, value: plusDI });
    minusDILine.push({ time: candles[i].time as UTCTimestamp, value: minusDI });
    
    dx[i] = plusDI + minusDI === 0 ? 0 : 100 * Math.abs(plusDI - minusDI) / (plusDI + minusDI);
  }

  const adxLine: LineData<UTCTimestamp>[] = [];
  let adxSum = 0;
  const adxStartIndex = period * 2 - 1;
  if (candles.length > adxStartIndex) {
    for (let i = period; i < adxStartIndex; i++) adxSum += dx[i];
    let prevAdx = adxSum / period;
    adxLine.push({ time: candles[adxStartIndex - 1].time as UTCTimestamp, value: prevAdx });
    
    for (let i = adxStartIndex; i < candles.length; i++) {
      prevAdx = (prevAdx * (period - 1) + dx[i]) / period;
      adxLine.push({ time: candles[i].time as UTCTimestamp, value: prevAdx });
    }
  }

  return { plusDI: plusDILine, minusDI: minusDILine, adx: adxLine };
}

export function supertrendData(candles: Candle[], atrPeriod: number = 10, multiplier: number = 3, upColor = '#26a69a', downColor = '#ef5350') {
  if (candles.length <= atrPeriod) return { line: [], markers: [] };
  
  const tr = new Array(candles.length).fill(0);
  for (let i = 1; i < candles.length; i++) {
    tr[i] = Math.max(
      candles[i].high - candles[i].low,
      Math.abs(candles[i].high - candles[i - 1].close),
      Math.abs(candles[i].low - candles[i - 1].close)
    );
  }

  const atr = new Array(candles.length).fill(0);
  atr[atrPeriod] = tr.slice(1, atrPeriod + 1).reduce((a, b) => a + b, 0) / atrPeriod;
  for (let i = atrPeriod + 1; i < candles.length; i++) {
    atr[i] = (atr[i - 1] * (atrPeriod - 1) + tr[i]) / atrPeriod; // RMA
  }

  const line: LineData<UTCTimestamp>[] = [];
  const markers: any[] = [];
  let trend = 1; // 1 up, -1 down
  let upperBand = 0, lowerBand = 0;
  let finalUpper = 0, finalLower = 0;

  for (let i = atrPeriod; i < candles.length; i++) {
    const hl2 = (candles[i].high + candles[i].low) / 2;
    const currentAtr = atr[i] * multiplier;
    upperBand = hl2 + currentAtr;
    lowerBand = hl2 - currentAtr;

    if (i === atrPeriod) {
      finalUpper = upperBand;
      finalLower = lowerBand;
    } else {
      finalUpper = upperBand < finalUpper || candles[i - 1].close > finalUpper ? upperBand : finalUpper;
      finalLower = lowerBand > finalLower || candles[i - 1].close < finalLower ? lowerBand : finalLower;
    }

    let flipped = false;
    if (trend === 1 && candles[i].close < finalLower) {
      trend = -1;
      flipped = true;
    } else if (trend === -1 && candles[i].close > finalUpper) {
      trend = 1;
      flipped = true;
    }

    const time = candles[i].time as UTCTimestamp;
    line.push({ 
      time, 
      value: trend === 1 ? finalLower : finalUpper,
      color: trend === 1 ? upColor : downColor
    });

    if (flipped) {
      markers.push({
        time,
        position: trend === 1 ? 'belowBar' : 'aboveBar',
        color: trend === 1 ? upColor : downColor,
        shape: trend === 1 ? 'arrowUp' : 'arrowDown',
        text: trend === 1 ? 'BUY' : 'SELL',
        size: 1
      });
    }
  }
  return { line, markers };
}
